// 9fbef606107a605d69c0edbcd8029e5d
import PropTypes from 'prop-types';
import React from 'react';
import { change } from 'redux-form';
import { formatMonthAndYear } from '@tcp/core/src/components/features/CnC/SinglePageCheckout/molecules/Payment/utils/Payment.utils';
import CHECKOUT_CONSTANTS from '@tcp/core/src/components/features/CnC/Checkout/Checkout.constants';
import Heading from '@tcp/core/src/components/common/atoms/Heading';
import checkoutLabels from '@tcp/core/src/utils/labels/cnc/checkout.labels';
import constants from '../container/CreditCard.constants';
import Button from '../../../../../../common/atoms/Button';
import Card from '../../../../../../common/molecules/Card';
import ErrorMessage from '../../../../common/molecules/ErrorMessage';
import {
  getLabelFromCopyOrFromTranslation,
  renderLabel,
} from '../../../../../../../utils/labels/labelUtilty';

const propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  className: PropTypes.string,
  cardList: PropTypes.shape({}).isRequired,
  onFileCardKey: PropTypes.string,
  initialValues: PropTypes.shape({}).isRequired,
  cvvCodeRichText: PropTypes.string,
  paymentMethodId: PropTypes.string.isRequired,
  orderHasShipping: PropTypes.bool,
  backLinkPickup: PropTypes.string.isRequired,
  backLinkShipping: PropTypes.string.isRequired,
  nextSubmitText: PropTypes.string.isRequired,
  isPaymentDisabled: PropTypes.bool,
  dispatch: PropTypes.func.isRequired,
  cardType: PropTypes.string,
  syncErrorsObj: PropTypes.shape({}),
  isGuest: PropTypes.bool,
  isSaveToAccountChecked: PropTypes.bool,
  selectedOnFileAddressId: PropTypes.string,
  userAddresses: PropTypes.shape({}),
  addressLabels: PropTypes.shape({}).isRequired,
  shippingAddress: PropTypes.shape({}),
  isSameAsShippingChecked: PropTypes.bool,
  billingData: PropTypes.shape({}),
  showAccordian: PropTypes.bool,
  isVenmoAppInstalled: PropTypes.bool,
  mapboxAutocompleteTypesParam: PropTypes.string,
  isEditMode: PropTypes.bool.isRequired,
};

const defaultProps = {
  className: '',
  onFileCardKey: '',
  cvvCodeRichText: null,
  orderHasShipping: false,
  isPaymentDisabled: false,
  cardType: null,
  syncErrorsObj: null,
  isGuest: true,
  isSaveToAccountChecked: false,
  selectedOnFileAddressId: null,
  userAddresses: null,
  shippingAddress: null,
  isSameAsShippingChecked: false,
  billingData: null,
  showAccordian: true,
  isVenmoAppInstalled: true,
  mapboxAutocompleteTypesParam: '',
};

const checkoutBillingLabelKey = 'checkout.billing';

const getCardOptions = ({
  creditCardList,
  onFileCardKey,
  addNewCCState,
  addNewCC,
  selectedCard,
}) => {
  const checkoutBillingLabels =
    getLabelFromCopyOrFromTranslation(checkoutBillingLabelKey, checkoutLabels) || {};
  let cardOptions = creditCardList.map((card) => ({
    value: card.creditCardId,
    title: `${renderLabel(
      checkoutBillingLabels.lbl_billing_creditCardEnd,
      'lbl_billing_creditCardEnd'
    )}${card.accountNo.slice(-4)} ${
      card.defaultInd
        ? `(${renderLabel(checkoutBillingLabels.lbl_billing_default, 'lbl_billing_default')})`
        : ''
    }`,
    content: (
      <Card
        card={card}
        isDefault={card.defaultInd}
        cardNumber={`${renderLabel(
          checkoutBillingLabels.lbl_billing_creditCardEnd,
          'lbl_billing_creditCardEnd'
        )}${card.accountNo.slice(-4)}`}
        selectedValue={+onFileCardKey}
        dataLocator="cardDetailCardDropDown"
      />
    ),
  }));

  cardOptions = cardOptions.push({
    value: '',
    title: renderLabel(
      checkoutBillingLabels.lbl_billing_addCreditHeading,
      'lbl_billing_addCreditHeading'
    ),
    content: (
      <Button
        className="addCreditCardBtn"
        fullWidth
        buttonVariation="variable-width"
        fill="BLACK"
        onClick={addNewCC}
        disabled={addNewCCState || !selectedCard}
        dataLocator="addCreditCardBtn"
      >
        {renderLabel(checkoutBillingLabels.lbl_billing_addCreditBtn, 'lbl_billing_addCreditBtn')}
      </Button>
    ),
  });

  return cardOptions;
};

const paymentActionChanged = (
  dispatch,
  formName = constants.FORM_NAME,
  paymentAction = CHECKOUT_CONSTANTS.PAYMENT_ACTION.ADD,
  selectedCard
) => {
  dispatch(change(formName, 'paymentAction', paymentAction));
  if (paymentAction === CHECKOUT_CONSTANTS.PAYMENT_ACTION.ADD) {
    dispatch(change(formName, 'cardNumber', ''));
    dispatch(change(formName, 'expMonth', ''));
    dispatch(change(formName, 'expYear', ''));
    dispatch(change(formName, 'cvvCode', ''));
    dispatch(change(formName, 'expMonthAndYear', ''));
    dispatch(change(formName, 'onFileCardKey', ''));
  }

  if (
    (paymentAction === CHECKOUT_CONSTANTS.PAYMENT_ACTION.EDIT ||
      paymentAction === CHECKOUT_CONSTANTS.PAYMENT_ACTION.SELECT) &&
    selectedCard
  ) {
    dispatch(change(formName, 'onFileCardKey', selectedCard?.creditCardId));
    dispatch(change(formName, 'cardType', selectedCard?.ccType?.toUpperCase()));
    dispatch(change(formName, 'cardNumber', selectedCard?.accountNo));
    dispatch(change(formName, 'expMonth', selectedCard?.expMonth));
    dispatch(change(formName, 'expYear', selectedCard?.expYear));
    dispatch(change(formName, `sameAsShipping`, false));
    dispatch(
      change(formName, 'saveToAccount', paymentAction === CHECKOUT_CONSTANTS.PAYMENT_ACTION.EDIT)
    );
    dispatch(
      change(
        formName,
        'defaultPayment',
        paymentAction === CHECKOUT_CONSTANTS.PAYMENT_ACTION.EDIT && selectedCard?.defaultInd
      )
    );
    dispatch(
      change(
        formName,
        'expMonthAndYear',
        formatMonthAndYear(selectedCard?.expMonth, selectedCard?.expYear)
      )
    );
  }
};

const onCCDropUpdateChange = (value, selectedCard, dispatch, formName = constants.FORM_NAME) => {
  paymentActionChanged(dispatch, formName, CHECKOUT_CONSTANTS.PAYMENT_ACTION.EDIT, selectedCard);
};

const onAddNewCreditCardUpdate = (dispatch, formName = constants.FORM_NAME) => {
  paymentActionChanged(dispatch, formName, CHECKOUT_CONSTANTS.PAYMENT_ACTION.ADD);
};

const getFormName = (editMode, form) => {
  return editMode ? constants.FORM_NAME : form;
};

const renderBillingAddressHeading = () => {
  const checkoutBillingLabels =
    getLabelFromCopyOrFromTranslation(checkoutBillingLabelKey, checkoutLabels) || {};
  return (
    <Heading
      component="h3"
      variant="listMenu"
      className="cardDropdownHeading"
      dataLocator="cardDropDownLbl"
    >
      {renderLabel(checkoutBillingLabels.lbl_billing_selectFromCard, 'lbl_billing_selectFromCard')}
    </Heading>
  );
};

const renderAddressError = (scope, isEspot = false) => {
  const checkoutBillingLabels =
    getLabelFromCopyOrFromTranslation(checkoutBillingLabelKey, checkoutLabels) || {};
  const { shippingAddress, selectedCountry } = scope.props;
  const isEnabledMessage =
    shippingAddress &&
    shippingAddress.country &&
    selectedCountry &&
    selectedCountry !== shippingAddress.country;

  const error = `${renderLabel(
    checkoutBillingLabels.lbl_billing_diffrentcountry_1,
    'lbl_billing_diffrentcountry_1'
  )} ${renderLabel(
    checkoutBillingLabels.lbl_billing_diffrentcountry_2,
    'lbl_billing_diffrentcountry_2'
  )} ${renderLabel(
    checkoutBillingLabels.lbl_billing_diffrentcountry_3,
    'lbl_billing_diffrentcountry_3'
  )}`;

  return isEnabledMessage ? (
    <ErrorMessage
      error={error}
      fontSize="fs12"
      fontWeight="extrabold"
      showAccordian
      isEspot={isEspot}
      noBackground
    />
  ) : null;
};

const renderInternationalBillingAddressError = () => {
  const checkoutBillingLabels =
    getLabelFromCopyOrFromTranslation(checkoutBillingLabelKey, checkoutLabels) || {};

  const error = `${renderLabel(
    checkoutBillingLabels.lbl_billing_diffrentcountry_1,
    'lbl_billing_diffrentcountry_1'
  )} ${renderLabel(
    checkoutBillingLabels.lbl_billing_diffrentcountry_2,
    'lbl_billing_diffrentcountry_2'
  )} ${renderLabel(
    checkoutBillingLabels.lbl_billing_diffrentcountry_3,
    'lbl_billing_diffrentcountry_3'
  )}`;

  return (
    <ErrorMessage
      error={error}
      fontSize="fs12"
      fontWeight="extrabold"
      showAccordian
      isEspot
      noBackground
    />
  );
};

/**
 * @description - Show Venmo if payment method is Venmo and Payments tab is enabled
 */
const showVenmoButton = (scope) => {
  const { paymentMethodId, isPaymentDisabled } = scope.props;
  return paymentMethodId === constants.PAYMENT_METHOD_VENMO && !isPaymentDisabled;
};

export {
  propTypes,
  defaultProps,
  getCardOptions,
  onCCDropUpdateChange,
  onAddNewCreditCardUpdate,
  getFormName,
  renderBillingAddressHeading,
  renderAddressError,
  showVenmoButton,
  paymentActionChanged,
  renderInternationalBillingAddressError,
};
